<template>
  <div>
    <b-modal
      v-model="isVisible"
      :title="modalTitle"
      hide-footer
      centered
      size="lg"
      content-class="file-selection-modal-content"
      @hidden="onHidden"
    >
      <div class="file-selection-modal">
        <!-- Empty State -->
        <div v-if="filteredFiles.length === 0" class="empty-state">
          <div class="empty-state-icon">
            <feather-icon :icon="emptyStateIcon" size="40" />
          </div>
          <h6>{{ $t('general.noFilesFound') }}</h6>
          <!-- <p class="text-muted">{{ $t('general.tryAgainLater') }}</p> -->
        </div>

        <!-- File List -->
        <div v-else class="file-list">
          <div
            v-for="(file, index) in filteredFiles"
            :key="index"
            class="file-item"
            :class="{ 'selected': selectedFileIndex === index }"
            @click="selectFile(index)"
          >
            <div class="file-item-inner">
              <!-- Selection Indicator -->
              <div class="file-selection">
                <div class="selection-indicator" :class="{ 'selected': selectedFileIndex === index }">
                  <feather-icon v-if="selectedFileIndex === index" icon="CheckIcon" size="16" />
                </div>
              </div>

              <!-- File Icon or Preview -->
              <div class="file-icon" :class="{
                'is-image': file.mimeType && file.mimeType.startsWith('image/') && file.url,
                'is-video': file.mimeType && file.mimeType.startsWith('video/') && file.url,
                'is-audio': file.mimeType && file.mimeType.startsWith('audio/') && file.url
              }">
                <img
                  v-if="file.mimeType && file.mimeType.startsWith('image/') && file.url"
                  :src="file.url"
                  class="preview-image"
                  alt="Preview"
                  @click.stop="openImagePreview(file)"
                />
                <div
                  v-if="file.mimeType && file.mimeType.startsWith('image/') && file.url"
                  class="zoom-icon-overlay"
                  @click.stop="openImagePreview(file)"
                >
                  <feather-icon icon="ZoomInIcon" size="16" />
                </div>
                <div
                  v-else-if="file.mimeType && file.mimeType.startsWith('video/') && file.url"
                  class="video-preview"
                  @click.stop="openVideoPreview(file)"
                >
                  <div class="default-video-thumbnail" :id="`video-thumbnail-${index}`">
                    <feather-icon icon="VideoIcon" size="20" />
                    <div class="thumbnail-loading">
                      <b-spinner variant="primary" small></b-spinner>
                    </div>
                  </div>
                  <div class="play-icon-overlay">
                    <feather-icon icon="PlayIcon" size="16" />
                  </div>
                </div>
                <div
                  v-else-if="file.mimeType && file.mimeType.startsWith('audio/') && file.url"
                  class="audio-preview"
                  @click.stop="openAudioPreview(file)"
                >
                  <div class="audio-thumbnail">
                    <feather-icon icon="MicIcon" size="20" />
                  </div>
                  <div class="play-icon-overlay">
                    <feather-icon icon="PlayIcon" size="16" />
                  </div>
                </div>
                <feather-icon
                  v-else
                  :icon="getFileIconByMimeType(file.mimeType)"
                  size="20"
                />
              </div>

              <!-- File Details -->
              <div class="file-details">
                <div class="file-name">{{ file.name }}</div>
                <div class="file-meta">{{ formatDate(file.date) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="modal-actions">
          <b-button
            variant="outline-secondary"
            @click="cancel"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="selectedFileIndex === null"
            @click="confirm"
          >
            {{ $t('button.select') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Video Preview Modal -->
    <video-preview-modal
      v-model="isVideoPreviewVisible"
      :video-url="selectedVideoUrl"
      :video-type="selectedVideoType"
    />

    <!-- Image Preview Modal -->
    <image-preview-modal
      v-model="isImagePreviewVisible"
      :image-url="selectedImageUrl"
      :image-alt="selectedImageAlt"
    />

    <!-- Audio Preview Modal -->
    <audio-preview-modal
      v-model="isAudioPreviewVisible"
      :audio-url="selectedAudioUrl"
      :audio-type="selectedAudioType"
    />
  </div>
</template>

<script>
import {
  BModal,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import VideoPreviewModal from '@/components/VideoPreviewModal.vue'
import ImagePreviewModal from '@/components/ImagePreviewModal.vue'
import AudioPreviewModal from '@/components/AudioPreviewModal.vue'

export default {
  name: 'FileSelectionModal',

  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    VideoPreviewModal,
    ImagePreviewModal,
    AudioPreviewModal,
  },

  props: {
    // Type of file to select (photo, video, voice, etc.)
    fileType: {
      type: String,
      default: '',
    },
    // Array of files to display
    files: {
      type: Array,
      default: () => [],
    },
    // Whether the modal is visible
    value: {
      type: Boolean,
      default: false,
    },
    // Currently selected file (if any)
    currentSelectedFile: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      selectedFileIndex: null,
      filteredFiles: [],
      isVideoPreviewVisible: false,
      selectedVideoUrl: '',
      selectedVideoType: '',
      isImagePreviewVisible: false,
      selectedImageUrl: '',
      selectedImageAlt: '',
      isAudioPreviewVisible: false,
      selectedAudioUrl: '',
      selectedAudioType: '',
    }
  },

  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    modalTitle() {
      if (!this.fileType) return this.$t('general.selectFile')

      const typeLabels = {
        photo: this.$t('field.selectPhoto'),
        video: this.$t('field.selectVideo'),
        voice: this.$t('field.selectVoice'),
      }

      return typeLabels[this.fileType] || this.$t('general.selectFile')
    },
    emptyStateIcon() {
      const iconMap = {
        photo: 'ImageIcon',
        video: 'VideoIcon',
        voice: 'MicIcon',
      }

      return iconMap[this.fileType] || 'FileIcon'
    },
  },

  watch: {
    files: {
      immediate: true,
      handler(newFiles) {
        this.filteredFiles = [...newFiles]
        // Generate thumbnails after DOM update
        this.$nextTick(() => {
          this.generateVideoThumbnails()
          this.preSelectCurrentFile()
        })
      },
    },
    value(newValue) {
      if (newValue) {
        this.filteredFiles = [...this.files]
        // Generate thumbnails after DOM update
        this.$nextTick(() => {
          this.generateVideoThumbnails()
          this.preSelectCurrentFile()
        })
      }
    },
    currentSelectedFile: {
      immediate: true,
      handler() {
        if (this.isVisible) {
          this.preSelectCurrentFile()
        }
      },
    },
  },

  methods: {
    selectFile(index) {
      this.selectedFileIndex = index
    },

    confirm() {
      if (this.selectedFileIndex === null) return

      const selectedFile = this.filteredFiles[this.selectedFileIndex]
      this.$emit('select', selectedFile)
      this.isVisible = false
    },

    cancel() {
      this.isVisible = false
      this.$emit('cancel')
    },

    onHidden() {
      this.selectedFileIndex = null
      this.$emit('hidden')
    },

    preSelectCurrentFile() {
      if (!this.currentSelectedFile || !this.filteredFiles.length) {
        return
      }

      // Find the index of the currently selected file in the filtered files array
      // We need to match by URL and mimeType since the file objects might be different instances
      let foundIndex = -1

      this.filteredFiles.forEach((file, index) => {
        if (file.url === this.currentSelectedFile.url
          && file.mimeType === this.currentSelectedFile.mimeType) {
          foundIndex = index
        }
      })

      if (foundIndex !== -1) {
        this.selectedFileIndex = foundIndex
      }
    },

    getFileIconByMimeType(mimeType) {
      if (!mimeType) return 'FileIcon'

      if (mimeType.startsWith('image/')) {
        return 'ImageIcon'
      }

      if (mimeType.startsWith('video/')) {
        return 'VideoIcon'
      }

      if (mimeType.startsWith('audio/')) {
        return 'MicIcon'
      }

      // Additional file type icons
      if (mimeType === 'application/pdf') {
        return 'FileTextIcon'
      }

      if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
        return 'FileIcon'
      }

      if (mimeType.includes('document') || mimeType.includes('word')) {
        return 'FileTextIcon'
      }

      return 'FileIcon'
    },

    formatDate(date) {
      if (!date) return ''

      try {
        const dateObj = new Date(date)
        return dateObj.toLocaleDateString(this.$i18n.locale || 'en', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      } catch (e) {
        return ''
      }
    },

    onVideoThumbnailError(event) {
      // This method is no longer needed since we're using a default thumbnail
      const element = event.target
      element.style.display = 'none'
      element.parentElement.classList.add('thumbnail-error')
    },

    openVideoPreview(file) {
      this.selectedVideoUrl = file.url
      this.selectedVideoType = file.mimeType
      this.isVideoPreviewVisible = true
    },

    openImagePreview(file) {
      this.selectedImageUrl = file.url
      this.selectedImageAlt = file.name || 'Image preview'
      this.isImagePreviewVisible = true
    },

    openAudioPreview(file) {
      this.selectedAudioUrl = file.url
      this.selectedAudioType = file.mimeType
      this.isAudioPreviewVisible = true
    },

    generateVideoThumbnails() {
      // Find all video files
      this.filteredFiles.forEach((file, index) => {
        if (file.mimeType && file.mimeType.startsWith('video/') && file.url) {
          const thumbnailContainer = document.getElementById(`video-thumbnail-${index}`)
          if (!thumbnailContainer) return

          // Create a temporary video element
          const video = document.createElement('video')
          video.style.display = 'none'
          video.crossOrigin = 'anonymous' // Try to avoid CORS issues
          video.src = file.url
          video.muted = true
          video.preload = 'metadata'

          // Set a timeout to handle cases where the video doesn't load properly
          const timeoutId = setTimeout(() => {
            console.warn('Thumbnail generation timed out')
            video.remove()
            // Keep the default icon visible

            // Remove loading indicator
            const loadingEl = thumbnailContainer.querySelector('.thumbnail-loading')
            if (loadingEl) loadingEl.remove()
          }, 5000) // 5 second timeout

          // When metadata is loaded, seek to a point in the video
          video.onloadedmetadata = () => {
            try {
              // Seek to 25% of the video duration for a good thumbnail
              const seekTime = Math.min(video.duration * 0.25, 5) // Cap at 5 seconds

              // For very short videos, use the middle
              if (video.duration < 2) {
                video.currentTime = video.duration / 2
              } else {
                video.currentTime = seekTime
              }
            } catch (error) {
              console.error('Error seeking video:', error)
              // Try a fixed position as fallback
              try {
                video.currentTime = 1.0 // Try 1 second in
              } catch (e) {
                // If all fails, remove loading indicator
                const loadingEl = thumbnailContainer.querySelector('.thumbnail-loading')
                if (loadingEl) loadingEl.remove()
              }
            }
          }

          // When the frame at the seek point is loaded
          video.onseeked = () => {
            try {
              clearTimeout(timeoutId) // Clear the timeout

              // Create a canvas to capture the frame
              const canvas = document.createElement('canvas')

              // Set canvas dimensions to create a square thumbnail
              canvas.width = 120 // Higher resolution for better quality
              canvas.height = 120

              // Draw the video frame to the canvas with cropping to square
              const ctx = canvas.getContext('2d')

              // Fill with background color first
              ctx.fillStyle = '#e2e8f0'
              ctx.fillRect(0, 0, canvas.width, canvas.height)

              // Draw the video frame centered and cropped
              if (video.videoWidth && video.videoHeight) {
                // Calculate the source rectangle for cropping
                let sx = 0
                let sy = 0
                let sWidth = 0
                let sHeight = 0

                if (video.videoWidth > video.videoHeight) {
                  // Landscape video - crop sides
                  sWidth = video.videoHeight
                  sHeight = video.videoHeight
                  sx = (video.videoWidth - video.videoHeight) / 2
                  sy = 0
                } else {
                  // Portrait or square video - crop top/bottom
                  sWidth = video.videoWidth
                  sHeight = video.videoWidth
                  sx = 0
                  sy = (video.videoHeight - video.videoWidth) / 2
                }

                // Draw the cropped video frame
                ctx.drawImage(
                  video,
                  sx, sy, sWidth, sHeight, // Source rectangle
                  0, 0, canvas.width, canvas.height, // Destination rectangle
                )
              }

              // Convert canvas to image with high quality
              const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.9)

              // Create and add the thumbnail image
              const img = document.createElement('img')
              img.src = thumbnailUrl
              img.className = 'generated-thumbnail'
              img.alt = 'Video thumbnail'

              // Clear the container and add the thumbnail
              while (thumbnailContainer.firstChild) {
                thumbnailContainer.removeChild(thumbnailContainer.firstChild)
              }
              thumbnailContainer.appendChild(img)

              // Remove loading indicator
              const loadingEl = thumbnailContainer.querySelector('.thumbnail-loading')
              if (loadingEl) loadingEl.remove()

              // Clean up
              video.remove()
            } catch (error) {
              console.error('Error generating thumbnail:', error)
              clearTimeout(timeoutId) // Clear the timeout
              video.remove()
              // Keep the default icon visible

              // Remove loading indicator
              const loadingEl = thumbnailContainer.querySelector('.thumbnail-loading')
              if (loadingEl) loadingEl.remove()
            }
          }

          // Handle errors
          video.onerror = () => {
            console.error('Error loading video for thumbnail generation')
            clearTimeout(timeoutId) // Clear the timeout
            video.remove()
            // Keep the default icon visible

            // Remove loading indicator
            const loadingEl = thumbnailContainer.querySelector('.thumbnail-loading')
            if (loadingEl) loadingEl.remove()
          }

          // Start loading the video
          document.body.appendChild(video)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.file-selection-modal {
  .empty-state {
    padding: 3rem 1rem;
    text-align: center;

    .empty-state-icon {
      width: 70px;
      height: 70px;
      margin: 0 auto 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f8f9fa;
      border-radius: 50%;
      color: #6c757d;
    }

    h6 {
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    p {
      color: #6c757d;
      max-width: 250px;
      margin: 0 auto;
    }
  }

  .file-list {
    max-height: 400px;
    overflow-y: auto;
    margin: 0 -0.5rem;
    padding: 0.5rem;
    border-radius: 8px;
    background-color: #f9fafb;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;
    }
  }

  .file-item {
    border-radius: 8px;
    margin-bottom: 0.75rem;
    cursor: pointer;
    transition: all 0.15s ease;
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &.selected {
      background-color: rgba(141, 81, 133, 0.08);
      border: 1px solid #8d5185;

      .file-name {
        color: #8d5185;
        font-weight: 500;
      }

      .file-icon {
        background-color: rgba(141, 81, 133, 0.15);
        color: #8d5185;
      }
    }

    &-inner {
      display: flex;
      align-items: center;
      padding: 0.875rem 1rem;
    }
  }

  .file-selection {
    margin-right: 0.875rem;

    .selection-indicator {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      border: 2px solid #dee2e6;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      background-color: white;

      &.selected {
        background-color: var(--primary);
        border-color: var(--primary);
        color: white;
      }
    }
  }

  .file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: #f1f3f5;
    color: #495057;
    margin-right: 0.875rem;
    flex-shrink: 0;
    overflow: hidden;

    .preview-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.is-image {
      width: 60px;
      height: 60px;
      position: relative;
      border-radius: 8px;
      overflow: hidden;

      .zoom-icon-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: 6px;
        color: white;
        cursor: pointer;
        transition: background-color 0.2s ease;
        z-index: 3;
        opacity: 0;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 60%);
          z-index: -1;
        }

        svg {
          filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5));
          transform: scale(1.2);
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.5);
        }
      }

      &:hover {
        .zoom-icon-overlay {
          opacity: 1;
        }
      }
    }

    &.is-video {
      width: 60px;
      height: 60px;
      position: relative;
      border-radius: 8px;
      overflow: hidden;

      .video-preview {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
      }

      .default-video-thumbnail {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #e2e8f0;
        color: #64748b;
        overflow: hidden;

        .generated-thumbnail {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          max-width: none;
          object-fit: cover;
          z-index: 1;
        }

        .thumbnail-loading {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(0, 0, 0, 0.2);
          z-index: 2;
        }
      }

      .play-icon-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: 6px;
        color: white;
        cursor: pointer;
        transition: background-color 0.2s ease;
        z-index: 3;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 60%);
          z-index: -1;
        }

        svg {
          filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5));
          transform: scale(1.2);
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.5);
        }
      }

      &.thumbnail-error {
        background-color: #f8d7da;

        .play-icon-overlay {
          opacity: 1;
          background-color: rgba(0, 0, 0, 0.1);
        }
      }
    }

    &.is-audio {
      width: 60px;
      height: 60px;
      position: relative;
      border-radius: 8px;
      overflow: hidden;

      .audio-preview {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
      }

      .audio-thumbnail {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #e2e8f0;
        color: #64748b;
        overflow: hidden;
      }

      .play-icon-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: 6px;
        color: white;
        cursor: pointer;
        transition: background-color 0.2s ease;
        z-index: 3;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 60%);
          z-index: -1;
        }

        svg {
          filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5));
          transform: scale(1.2);
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.5);
        }
      }
    }
  }

  .file-details {
    flex: 1;
    min-width: 0;
  }

  .file-name {
    font-size: 0.95rem;
    font-weight: 500;
    color: #212529;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .file-meta {
    font-size: 0.8rem;
    color: #6c757d;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;

    .btn {
      min-width: 100px;
      font-weight: 500;

      &:not(:last-child) {
        margin-right: 0.75rem;
      }
    }
  }
}
</style>

<style lang="scss">
.file-selection-modal-content {
  border-radius: 12px;
  overflow: hidden;

  .modal-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;

    .modal-title {
      font-weight: 600;
      font-size: 1.1rem;
    }

    .close {
      font-size: 1.5rem;
      padding: 0.5rem;
      margin: -0.5rem -0.5rem -0.5rem auto;
      opacity: 0.5;
      transition: opacity 0.15s ease;

      &:hover {
        opacity: 0.75;
      }
    }
  }

  .modal-body {
    padding: 1.5rem;
  }
}
</style>
